import { useEffect, useRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

export function Field({
  label, error, hint, optional, children, htmlFor,
}: {
  label: string; error?: string; hint?: string; optional?: boolean; children: ReactNode; htmlFor: string;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>
        {label}
        {optional && <span className="optional"> — facoltativo</span>}
      </label>
      {children}
      {hint && !error && <p className="muted">{hint}</p>}
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}

export function TextField({
  label, error, hint, optional, id, ...props
}: { label: string; error?: string; hint?: string; optional?: boolean; id: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Field label={label} error={error} hint={hint} optional={optional} htmlFor={id}>
      <input id={id} aria-invalid={error ? 'true' : undefined} {...props} />
    </Field>
  );
}

export function SelectField({
  label, error, hint, id, children, ...props
}: { label: string; error?: string; hint?: string; id: string; children: ReactNode } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <Field label={label} error={error} hint={hint} htmlFor={id}>
      <select id={id} aria-invalid={error ? 'true' : undefined} {...props}>
        {children}
      </select>
    </Field>
  );
}

export function Alert({ kind = 'info', children }: { kind?: 'info' | 'error' | 'warn'; children: ReactNode }) {
  return (
    <div className={`alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Caricamento' }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

/**
 * A sheet that rises from the bottom, within thumb reach. Built on <dialog> so focus, Escape and
 * the backdrop come from the platform rather than from code that can get them wrong.
 */
export function Sheet({
  open, onClose, title, children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // jsdom has no showModal; there the sheet simply renders in place.
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-label={title}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      // A tap on the backdrop lands on the dialog element itself, not on its content.
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {open && (
        <div className="sheet-body">
          <span className="sheet-grip" aria-hidden="true" />
          {title && <h2 className="sheet-title">{title}</h2>}
          {children}
        </div>
      )}
    </dialog>
  );
}

/** A short-lived confirmation at the bottom of the screen, announced to screen readers. */
export function Toast({ message }: { message: string | null }) {
  return (
    <div className={`toast${message ? ' show' : ''}`} role="status" aria-live="polite">
      {message}
    </div>
  );
}
